import { Box, Icon, Typography } from '@mui/material';
import {
    CanvaFooterIcon,
    FlutterIcon,
    GraphQLIcon,
    JavaScriptIcon,
    MaterialUIIcon,
    PostgresqlIcon,
    adobeIcon,
    awsIcon,
    figmaIcon,
    nodeJsIcon,
    reactIcon,
    springBootIcon,
    typeScriptIcon,
} from '../../resources/icons/index';
import React, { useLayoutEffect, useRef, useState } from 'react';

// List of technology items
const baseTechnologies = [
    { icon: awsIcon, title: 'Amazon' },
    { icon: springBootIcon, title: 'SpringBoot' },
    { icon: reactIcon, title: 'ReactJS' },
    { icon: typeScriptIcon, title: 'TypeScript' },
    { icon: figmaIcon, title: 'Figma' },
    { icon: adobeIcon, title: 'Adobe Illustrator' },
    { icon: nodeJsIcon, title: 'NodeJS' },
    { icon: CanvaFooterIcon, title: 'Canva' },
    { icon: JavaScriptIcon, title: 'JavaScript' },
    { icon: PostgresqlIcon, title: 'Postgresql' },
    { icon: MaterialUIIcon, title: 'Material-UI' },
    { icon: GraphQLIcon, title: 'GraphQL' },
    { icon: FlutterIcon, title: 'Flutter' },
];

// Reusable Technology Item Component
const TechnologyItem: React.FC<{ icon: React.ElementType; title: string }> = ({ icon, title }) => (
    <Box
        sx={{
            alignItems: 'center',
            display: 'flex',
            flexShrink: 0,
            gap: '10px',
            paddingRight: '30px',
        }}
    >
        <Icon component={icon} />
        <Typography variant="subtitle2">{title}</Typography>
    </Box>
);

const TechnologyFooter: React.FC = () => {
    // We'll compute how many copies of baseTechnologies are needed to cover the container.
    const [duplicationFactor, setDuplicationFactor] = useState(1);
    // scrollDistance: the width of one complete set (the first clone)
    const [scrollDistance, setScrollDistance] = useState(0);
    // Animation duration in seconds (using a constant speed; here 100px per second)
    const [animationDuration, setAnimationDuration] = useState(15);

    // Refs for container, for the base set measurement, and for the full set clone.
    const containerRef = useRef<HTMLDivElement>(null);
    const baseSetRef = useRef<HTMLDivElement>(null);
    const firstCloneRef = useRef<HTMLDivElement>(null);

    // On mount and on resize, measure the base set and compute duplication factor.
    useLayoutEffect(() => {
        const recalcDuplication = (): void => {
            if (containerRef.current && baseSetRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                const baseWidth = baseSetRef.current.offsetWidth;
                // Compute how many copies are needed to cover the container plus one extra.
                const factor = Math.ceil(containerWidth / baseWidth) + 1;
                setDuplicationFactor(factor);
            }
        };
        recalcDuplication();
        window.addEventListener('resize', recalcDuplication);
        return () => window.removeEventListener('resize', recalcDuplication);
    }, []);

    // Build the full set based on duplicationFactor.
    const fullSet = Array.from({ length: duplicationFactor }, () => baseTechnologies).flat();

    // Once the full set is rendered, measure its width.
    useLayoutEffect(() => {
        if (firstCloneRef.current) {
            const measuredWidth = firstCloneRef.current.offsetWidth;
            setScrollDistance(measuredWidth);
            // Set the animation duration at a rate of 100px per second.
            setAnimationDuration(measuredWidth / 60);
        }
    }, [fullSet]);

    return (
        <Box
            ref={containerRef}
            sx={{
                bgcolor: 'text.hint',
                marginY: { xs: '25px', xxl: '60px' },
                overflow: 'hidden',
                position: 'relative',
                width: '100%',
            }}
        >
            {/* Off-screen base set for measurement */}
            <Box
                ref={baseSetRef}
                sx={{
                    display: 'flex',
                    opacity: 0,
                    position: 'absolute',
                    whiteSpace: 'nowrap',
                }}
            >
                {baseTechnologies.map((tech) => (
                    <TechnologyItem key={`base-${tech.title}`} {...tech} />
                ))}
            </Box>
            {/* Injects dynamic keyframes based on calculated scroll distance */}
            <style>
                {`
                    @keyframes scroll {
                        0% { transform: translateX(0); }
                        100% { transform: translateX(-${scrollDistance}px); }
                    }
                `}
            </style>

            {/* Scrolling container */}
            <Box
                sx={{
                    '&:hover': { animationPlayState: 'paused' },
                    animation: `scroll ${animationDuration}s linear infinite`,
                    display: 'flex',
                    padding: '24px',
                    whiteSpace: 'nowrap',
                }}
            >
                {/* First clone - determines scroll distance */}
                <Box ref={firstCloneRef} sx={{ display: 'flex' }}>
                    {fullSet.map((tech, index) => (
                        <TechnologyItem key={`first-${tech.title}-${index}`} {...tech} />
                    ))}
                </Box>

                {/* Second clone for seamless looping */}
                <Box sx={{ display: 'flex' }}>
                    {fullSet.map((tech, index) => (
                        <TechnologyItem key={`clone-${tech.title}-${index}`} {...tech} />
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default TechnologyFooter;
